import { useMemo } from "react";
import Alert from "react-bootstrap/Alert";
import { resources } from "@constants/hardcoded-strings";
import { ASSERTION_SCORE_SERVICE_OFFERING_ID, TRUST_SCORE_SERVICE_OFFERING_ID } from "@constants/verification-service-details-constants";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { ServicesServiceOfferingValidationErrors } from "@idw-types/common/validation/verification-service-details-validation-errors";
import {
    FormattedRpServiceConfiguration,
    FormattedRpServiceConfigurationServiceOffering
} from "@idw-types/data/rp-service-configuration";
import { ContentPageBody } from "../../../../common/content-page-body/content-page-body";
import { ContentPageHeader } from "../../../../common/content-page-header/content-page-header";
import { RpServiceConfigurationServiceOfferingsContainer } from "../../../../common/rp-service-configuration-service-offerings-container/rp-service-configuration-service-offerings-container";
import { getSelectedServiceOfferings, getUnselectedServiceOfferings } from "@utils/service-offerings/service-offerings-utils";
import commonStyles from "../../verification-service-details-component.less";
import { ApAssertionStatusMap } from "@idw-types/data/ap-assertion-status-map/ap-assertion-status-map";

type EnvironmentalTabContainerProps = {
    isDisabled: boolean;
    validationErrors: ServicesServiceOfferingValidationErrors;
    initialRpServiceConfiguration: FormattedRpServiceConfiguration;
    rpServiceConfigurationState: FormattedRpServiceConfiguration;
    assertionStatusMaps: Array<ApAssertionStatusMap>;
    assertionScoreServiceOfferingIDs: Array<string>;
    trustScoreServiceOfferingIDs: Array<string>;
    scoreServiceOfferingIDs: Array<string>;
    createHeaderButton: (serviceOfferings: Array<FormattedRpServiceConfigurationServiceOffering>, maxOrder: number) => React.ReactNode;
    setRpServiceConfigurationState: (state: FormattedRpServiceConfiguration) => void;
};

export const EnvironmentalTabContainer = (props: EnvironmentalTabContainerProps) => {
    const {
        isDisabled,
        validationErrors,
        initialRpServiceConfiguration,
        rpServiceConfigurationState,
        assertionStatusMaps,
        assertionScoreServiceOfferingIDs,
        trustScoreServiceOfferingIDs,
        scoreServiceOfferingIDs,
        createHeaderButton,
        setRpServiceConfigurationState
    } = props;

    const environmentalServiceOfferingIds = useMemo(() => {
        return rpServiceConfigurationState.serviceOfferings
            .filter(so =>
                so.category === "ENVIRONMENT" &&
                so.serviceOfferingStatus === EntityStatusTypes.ACTIVE &&
                ![ASSERTION_SCORE_SERVICE_OFFERING_ID, TRUST_SCORE_SERVICE_OFFERING_ID].includes(so.serviceOfferingID)
            )
            .map(so => so.serviceOfferingID);
    }, []);

    //todo - need to verify no permissions are necessary to add/remove service offerings
    const environmentalServiceOfferings = rpServiceConfigurationState.serviceOfferings.filter(so => environmentalServiceOfferingIds.includes(so.serviceOfferingID));

    const selectedServiceOfferings = useMemo(() => {
        return getSelectedServiceOfferings(rpServiceConfigurationState.serviceOfferings, environmentalServiceOfferingIds);
    }, [rpServiceConfigurationState.serviceOfferings, environmentalServiceOfferingIds]);

    const unselectedServiceOfferings = useMemo(() => {
        return getUnselectedServiceOfferings(rpServiceConfigurationState.serviceOfferings, selectedServiceOfferings, environmentalServiceOfferingIds);
    }, [rpServiceConfigurationState.serviceOfferings, selectedServiceOfferings, environmentalServiceOfferingIds]);

    let maxOrder = 0;
    selectedServiceOfferings.forEach(so => {
        if (so.order && so.order >= maxOrder) {
            maxOrder = so.order + 1;
        }
    });

    const headerButton = createHeaderButton(unselectedServiceOfferings, maxOrder);

    return (
        <div>
            <div>
                <ContentPageHeader
                    title={resources.pages.verificationServiceDetails.tabs.services.subTabs.environmental.title}
                    headerButton={headerButton}
                />
                <ContentPageBody isMaxWidth={true}>
                    <span className={commonStyles.description}>{resources.pages.verificationServiceDetails.tabs.services.subTabs.environmental.description}</span>
                    <div>
                        {validationErrors.servicesError}
                        {unselectedServiceOfferings.length === 0 && (
                            <Alert variant={"info"}>{resources.pages.verificationServiceDetails.tabs.services.subTabs.common.alerts.allServiceOfferingsMessage(
                                resources.pages.verificationServiceDetails.tabs.services.subTabs.environmental.serviceOfferingCategory
                            )}</Alert>
                        )}
                        {selectedServiceOfferings.length === 0 && (
                            <Alert variant={"info"}>{resources.pages.verificationServiceDetails.tabs.services.subTabs.common.alerts.noServiceOfferingsMessage(
                                resources.pages.verificationServiceDetails.tabs.services.subTabs.environmental.serviceOfferingCategory,
                                isDisabled
                            )}</Alert>
                        )}
                        {selectedServiceOfferings.length > 0 && (
                            <RpServiceConfigurationServiceOfferingsContainer
                                isDisabled={isDisabled}
                                assertionStatusMaps={assertionStatusMaps}
                                assertionScoreServiceOfferingIDs={assertionScoreServiceOfferingIDs}
                                trustScoreServiceOfferingIDs={trustScoreServiceOfferingIDs}
                                scoreServiceOfferingIDs={scoreServiceOfferingIDs}
                                initialRpServiceConfiguration={initialRpServiceConfiguration}
                                selectedRpServiceConfigurationServiceOfferings={selectedServiceOfferings}
                                rpServiceConfigurationServiceOfferings={environmentalServiceOfferings}
                                rpServiceConfigurationState={rpServiceConfigurationState}
                                setRpServiceConfigurationState={setRpServiceConfigurationState}
                            />
                        )}
                    </div>
                </ContentPageBody>
            </div>
        </div>
    );
};